import React, { useContext } from 'react';
import { NextPageContext } from 'next';

import AppLayout from '@layouts/AppLayout';

import { StateContext } from '@store/store';
import useLazyload from '@hooks/useLazyload';

import {
  BrowserProps,
  BlogHomepageCardDetailsType,
  CategoryType,
  BlogCardProps,
  BlogHomepageCardPlatformType,
  HeroSectionHeadingResponseType,
} from 'src/types';

import HeaderWithCTA from '@components/header-with-cta';

import ivclass from '@invideoio/web-shared/Modules/IVClass';

import {
  fetchAllProfessions,
  fetchHomepageHeroSectionTitle,
  fetchAllTrendingList,
} from '@lib/API/';

import { fetchAllCategories } from '@lib/API/categoryAPI';

import { fetchAllPlatforms } from '@lib/API/platformAPI';

import HomePageSearch from '@components/HomePageSearch';
import ErrorBoundary from '@components/ErrorBoundary';
import Platforms from '@components/platforms';
import CategoriesTray from '@components/categories';
import ProfessionsTray from '@components/professions';
import TrendingPostsTray from '@components/trending-posts';

import { ROUTES } from '@constants/common';
import BlogSEO from '@components/SEO/BlogSEO';
import useBlogAppLoad from '@hooks/useBlogAppLoad';
import useTranslation from 'next-translate/useTranslation';
import RecentPosts from '@components/recent-posts/RecentPosts';
import FixedBanner from '@invideoio/web-shared/components/FixedBanner';

interface HomeProps extends BrowserProps {
  platformList: BlogHomepageCardPlatformType[];
  professionList: BlogHomepageCardDetailsType[];
  headingGroup: HeroSectionHeadingResponseType;
  categoryList: CategoryType[];
  trendingList: BlogCardProps[];
}

const Home = (props: HomeProps) => {
  const {
    platformList,
    professionList,
    headingGroup,
    categoryList,
    trendingList,
  } = props;
  const globalState = useContext(StateContext);
  const { platform, browser } = globalState;

  const { t, lang } = useTranslation('common');

  useLazyload();

  useBlogAppLoad({ platform });

  return (
    <AppLayout platform={platform}>
      <BlogSEO
        seo={headingGroup.SEO}
        url={`${lang === 'en' ? '' : '/' + lang}/blog/`}
      />
      <HeaderWithCTA />
      <ErrorBoundary>
        {headingGroup && <HomePageSearch headingGroup={headingGroup.Heading} />}
      </ErrorBoundary>

      {Array.isArray(trendingList) && trendingList.length > 0 && (
        <div className={ivclass('iv-bg-black-4')}>
          <div className={ivclass('iv-container', 'iv-mx-auto')}>
            <ErrorBoundary>
              <section
                className={ivclass(
                  'iv-pt-[91px] lg:iv-pt-[91px] md:iv-pt-[50px] sm:iv-pt-40',
                  'iv-pb-100 lg:iv-pb-100 md:iv-pb-50 sm:iv-pb-25',
                  'lg:iv-pl-[43px] md:iv-pl-[25px] sm:iv-pl-[20px]',
                  'iv-overflow-x-hidden',
                )}
              >
                <TrendingPostsTray
                  trendingList={trendingList}
                  title={`${t('Top trending')} 🔥`}
                  viewAllURL={ROUTES.TRENDING}
                  platform={platform}
                  browser={browser}
                />
              </section>
            </ErrorBoundary>
          </div>
        </div>
      )}

      {lang !== 'en' && <RecentPosts />}
      {Array.isArray(platformList) && platformList.length > 0 && (
        <div>
          <div
            className={ivclass(
              'iv-container',
              'iv-mx-auto',
              'iv-overflow-x-hidden',
            )}
          >
            <ErrorBoundary>
              <Platforms
                platformList={platformList}
                SubHeadingTitle={'Platforms'}
                viewAllURL={ROUTES.PLATFORMS}
                platform={platform}
                browser={browser}
              />
            </ErrorBoundary>
          </div>
        </div>
      )}

      {Array.isArray(professionList) && professionList.length > 0 && (
        <div>
          <div
            className={ivclass(
              'iv-container',
              'iv-mx-auto',
              'iv-overflow-x-hidden',
            )}
          >
            <ErrorBoundary>
              <section
                className={ivclass(
                  'iv-mt-[42px] sm:iv-mt-5',
                  'iv-pb-[118px] md:iv-pb-[74px] sm:iv-pb-[24px]',
                  'lg:iv-pl-[43px] md:iv-pl-[18px] sm:iv-pl-[21px]',
                )}
              >
                <ProfessionsTray
                  professionList={professionList}
                  title={'Browse by professions'}
                  viewAllURL={''}
                  platform={platform}
                  browser={browser}
                />
              </section>
            </ErrorBoundary>
          </div>
        </div>
      )}

      {Array.isArray(categoryList) && categoryList.length > 0 && (
        <div>
          <div
            className={ivclass(
              'iv-container',
              'iv-mx-auto',
              'iv-overflow-x-hidden',
            )}
          >
            <ErrorBoundary>
              <section
                className={ivclass(
                  'iv-pt-15 md:iv-pt-[16px] sm:iv-pt-30',
                  'iv-pb-100 md:iv-pb-[75px] sm:iv-pb-50',
                  'lg:iv-pl-[43px] md:iv-pl-[25px] sm:iv-pl-[21px]',
                )}
              >
                <CategoriesTray
                  categoryList={categoryList}
                  subHeading={'Popular categories'}
                  viewAllURL={ROUTES.CATEGORIES}
                  platform={platform}
                  browser={browser}
                />
              </section>
            </ErrorBoundary>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

Home.getInitialProps = async (ctx: NextPageContext) => {
  try {
    const lang = ctx.locale || 'EN';
    const platformListPromise = fetchAllPlatforms({ language: lang });

    const categoryListPromise = fetchAllCategories({ language: lang });

    const professionListPromise = fetchAllProfessions({ language: lang });

    const headingGroupPromise = fetchHomepageHeroSectionTitle(
      '/blog-homepage-heading',
      lang,
    );

    const trendingListPromise = fetchAllTrendingList({
      start: 0,
      limit: 9,
      language: lang,
    });

    const [
      platformList,
      categoryList,
      professionList,
      headingGroup,
      trendingList,
    ] = await Promise.all([
      platformListPromise,
      categoryListPromise,
      professionListPromise,
      headingGroupPromise,
      trendingListPromise,
    ]);

    return {
      platformList,
      headingGroup,
      categoryList,
      professionList,
      trendingList,
    };
  } catch (error: any) {
    console.log(error.message);
  }
};

export default Home;
